import { useEffect, useRef } from 'react';

/**
 * Drive a depth-layered scene from pointer position and scroll offset.
 *
 * Design constraints this shape exists to satisfy:
 *
 * - ONE rAF loop and ONE style write per frame, on the scene root only. Each
 *   layer reads the same three custom properties and multiplies them by its own
 *   `--depth`, so adding a layer costs a DOM node and no extra JavaScript.
 * - Nothing is written unless a value actually changed, so an idle hero settles
 *   to zero work rather than repainting forever.
 * - Pointer parallax is opt-in by INPUT TYPE, not by screen size: a coarse
 *   pointer has no hover position to track, and reading one would pin the scene
 *   to wherever the last tap landed.
 * - Reduced motion disables the loop entirely and never attaches a listener —
 *   the calm is the point, and for some people motion is the opposite of calm.
 *
 * The ambient breathing zoom is NOT here: it is a CSS keyframe on the inner art
 * element, so it keeps running on the compositor without JavaScript, and it can
 * never fight this transform because they are applied to different elements.
 */

const POINTER_RANGE = 0.5; // how far from centre, in units, the pointer can push
const SCROLL_RANGE_PX = 640; // scroll distance over which the scene fully parallaxes
const EPSILON = 0.0005; // below this, a change is not worth a style write

function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}

export function useParallaxScene({ enabled = true } = {}) {
    const sceneRef = useRef(null);

    useEffect(() => {
        const sceneElement = sceneRef.current;

        if (!enabled || !sceneElement || typeof window === 'undefined') {
            return undefined;
        }

        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        const hasFinePointer = window.matchMedia('(pointer: fine)');

        if (prefersReducedMotion.matches) {
            return undefined;
        }

        const target = { x: 0, y: 0, scroll: 0 };
        const current = { x: 0, y: 0, scroll: 0 };
        let frameHandle = null;

        const scrollContainer = sceneElement.closest('.app-shell__content') || window;

        const readScroll = () => {
            const offset = scrollContainer === window
                ? window.scrollY
                : scrollContainer.scrollTop;

            target.scroll = clamp(offset / SCROLL_RANGE_PX, 0, 1);
            schedule();
        };

        const readPointer = (event) => {
            const { innerWidth, innerHeight } = window;

            target.x = clamp((event.clientX / innerWidth - 0.5) * 2, -1, 1) * POINTER_RANGE;
            target.y = clamp((event.clientY / innerHeight - 0.5) * 2, -1, 1) * POINTER_RANGE;
            schedule();
        };

        const releasePointer = () => {
            target.x = 0;
            target.y = 0;
            schedule();
        };

        function step() {
            frameHandle = null;

            // Critically damped-ish easing: the scene never snaps to the cursor,
            // it drifts toward it. This is what makes fast mouse movement read as
            // calm rather than twitchy.
            current.x += (target.x - current.x) * 0.06;
            current.y += (target.y - current.y) * 0.06;
            current.scroll += (target.scroll - current.scroll) * 0.12;

            sceneElement.style.setProperty('--parallax-x', current.x.toFixed(4));
            sceneElement.style.setProperty('--parallax-y', current.y.toFixed(4));
            sceneElement.style.setProperty('--parallax-scroll', current.scroll.toFixed(4));

            const atRest = Math.abs(target.x - current.x) < EPSILON
                && Math.abs(target.y - current.y) < EPSILON
                && Math.abs(target.scroll - current.scroll) < EPSILON;

            // Stop scheduling once the scene has caught up. The next pointer or
            // scroll event restarts the loop, so an untouched hero costs nothing.
            if (!atRest) {
                schedule();
            }
        }

        function schedule() {
            if (frameHandle === null) {
                frameHandle = window.requestAnimationFrame(step);
            }
        }

        scrollContainer.addEventListener('scroll', readScroll, { passive: true });

        if (hasFinePointer.matches) {
            window.addEventListener('pointermove', readPointer, { passive: true });
            window.addEventListener('pointerleave', releasePointer, { passive: true });
        }

        readScroll();

        return () => {
            if (frameHandle !== null) {
                window.cancelAnimationFrame(frameHandle);
            }

            scrollContainer.removeEventListener('scroll', readScroll);
            window.removeEventListener('pointermove', readPointer);
            window.removeEventListener('pointerleave', releasePointer);
        };
    }, [enabled]);

    return sceneRef;
}
